import React, { useState } from "react";
import { Modal, Spinner } from "reactstrap";
import ServerIpNotice from "./ServerIpNotice";

const NAVY = "#0A316E";

const LogoTile = ({ integration }) => (
    <div
        className="d-flex align-items-center justify-content-center bg-white shadow-sm"
        style={{ width: 48, height: 48, borderRadius: 12, overflow: "hidden", flexShrink: 0 }}
    >
        {integration.logo ? (
            <img src={integration.logo} alt={integration.name} style={{ maxWidth: "80%", maxHeight: "80%", objectFit: "contain" }} />
        ) : (
            <i className={`bx ${integration.icon || "bx-plug"}`} style={{ fontSize: 24, color: integration.accent || NAVY }} />
        )}
    </div>
);

/**
 * Entegrasyon bağla/yapılandır modal kabuğu.
 *
 * Props:
 *  - isOpen, toggle
 *  - integration        : registry meta (logo/ikon/ad)
 *  - title, subtitle    : başlık satırları
 *  - tabs               : [{ id, label }] verilirse segment nav gösterilir
 *  - children           : ReactNode VEYA (activeTab) => ReactNode
 *  - primaryLabel, onPrimary, primaryLoading, primaryIcon
 *  - secondaryLabel, onSecondary, secondaryVariant ("danger"|"light"), secondaryLoading
 */
export default function IntegrationConfigModal({
    isOpen,
    toggle,
    integration,
    title,
    subtitle,
    tabs,
    children,
    primaryLabel,
    onPrimary,
    primaryLoading = false,
    primaryIcon = "bx-save",
    secondaryLabel,
    onSecondary,
    secondaryVariant = "light",
    secondaryIcon,
    secondaryLoading = false,
}) {
    const [activeTab, setActiveTab] = useState(tabs?.[0]?.id);

    const body = typeof children === "function" ? children(activeTab) : children;

    const secondaryStyle =
        secondaryVariant === "danger"
            ? { color: "#dc3545", borderColor: "#f1aeb5", backgroundColor: "#fff" }
            : { color: "#5c6a86", borderColor: "#dfe3ea", backgroundColor: "#fff" };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="lg" contentClassName="border-0 rounded-4 overflow-hidden">
            {/* Başlık */}
            <div className="d-flex align-items-center justify-content-between p-4 border-bottom border-light">
                <div className="d-flex align-items-center gap-3">
                    {integration && <LogoTile integration={integration} />}
                    <div>
                        <h5 className="mb-0 fw-bold" style={{ color: "#1f2937" }}>{title}</h5>
                        {subtitle && <small className="text-muted">{subtitle}</small>}
                    </div>
                </div>
                <button
                    type="button"
                    className="btn btn-sm d-inline-flex align-items-center justify-content-center border"
                    style={{ borderRadius: 10, width: 38, height: 38, borderColor: "#e7eaf0", color: "#5c6a86" }}
                    onClick={toggle}
                    aria-label="Kapat"
                >
                    <i className="bx bx-x fs-4" />
                </button>
            </div>

            {/* Sekme navigasyonu (opsiyonel) */}
            {tabs && tabs.length > 0 && (
                <div className="px-4 pt-4">
                    <div className="d-flex p-1" style={{ backgroundColor: "#f4f6f9", borderRadius: 12 }}>
                        {tabs.map((t) => {
                            const isActive = activeTab === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setActiveTab(t.id)}
                                    className="btn btn-sm flex-fill fw-semibold border-0"
                                    style={{
                                        borderRadius: 9,
                                        padding: ".5rem",
                                        backgroundColor: isActive ? "#fff" : "transparent",
                                        color: isActive ? NAVY : "#8892a6",
                                        boxShadow: isActive ? "0 1px 3px rgba(10,49,110,.12)" : "none",
                                    }}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Gövde — sunucu IP bilgisi her entegrasyonun yapılandırma
                ekranında ortak olduğu için kabukta çizilir. */}
            <div className="p-4">
                <ServerIpNotice />
                {body}
            </div>

            {/* Footer (primary/secondary verilmezse gizlenir — ör. gövde kendi butonunu taşır) */}
            {(primaryLabel || secondaryLabel) && (
            <div className="d-flex align-items-center justify-content-between p-4 border-top border-light">
                {secondaryLabel ? (
                    <button
                        type="button"
                        className="btn d-inline-flex align-items-center gap-1 fw-semibold"
                        style={{ borderRadius: 999, padding: ".5rem 1.1rem", border: "1px solid", ...secondaryStyle }}
                        onClick={onSecondary}
                        disabled={secondaryLoading}
                    >
                        {secondaryLoading ? (
                            <Spinner size="sm" />
                        ) : secondaryIcon ? (
                            <i className={`bx ${secondaryIcon}`} />
                        ) : secondaryVariant === "danger" ? (
                            <i className="bx bx-unlink" />
                        ) : null}
                        {secondaryLabel}
                    </button>
                ) : <span />}

                <button
                    type="button"
                    className="btn d-inline-flex align-items-center gap-1 fw-semibold"
                    style={{ borderRadius: 999, padding: ".55rem 1.3rem", backgroundColor: NAVY, borderColor: NAVY, color: "#fff" }}
                    onClick={onPrimary}
                    disabled={primaryLoading}
                >
                    {primaryLoading ? <Spinner size="sm" /> : <i className={`bx ${primaryIcon}`} />}
                    {primaryLabel}
                </button>
            </div>
            )}
        </Modal>
    );
}
